import { useEffect, useState } from 'preact/hooks';
import {
  createRecord,
  getPlan,
  linkRecord,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  planHas,
  unlinkRecord,
  updateRecord,
  uploadHeadshot,
  type LinkRow,
  type PlanApi,
  type RecordRow,
} from '../lib/api';
import { activeOnly } from '../lib/retired';
import { AA, LINK, otherEnd } from '../lib/auditionSpread';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { bumpRecordsVersion, openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { RecordImage } from '../components/RecordMedia';
import { requestDocumentCapture } from '../components/CaptureSheet';
import {
  assembleCredits,
  castResume,
  creditLine,
  crewResume,
  resumeBlocks,
  resumeVariants,
  RESUME_BLOCKS,
  type Credit,
  type ResumeBlock,
  type ResumeVariant,
} from '../lib/resume';
import {
  catalogueFrom,
  layoutFromCatalogue,
  moveSection,
  parseLayout,
  placeRow,
  printableSections,
  resolveVersion,
  toggleRow,
  versionName,
  type ResolvedSection,
  type ResumeCatalogue,
  type ResumeVersionLayout,
} from '../lib/resumeVersion';
import {
  faceClearedReceipt,
  faceReceipt,
  lookName,
  looksFrom,
  movedReceipt,
  placedReceipt,
  printsLine,
  savedReceipt,
  toggledReceipt,
} from '../lib/resumeProfile';
import './resume.css';

// A10 — THE RESUME, WHICH IS THE OTHER END OF THE CLAIM.
//
// "This is the one app you need to manage your career from the very first
//  call until a project wraps." (Huan, 2026-08-22)
//
// Everything before this screen is entered once, as work. This is that same
// work said back in the form the industry asks for it, and NOTHING ON IT IS
// TYPED TWICE — there is no "add a credit" here and there never will be. A
// credit that can be typed can disagree with the work, and the first time it
// does the product is lying on the one page an actor hands to a stranger.
//
// ===================================================================
// 2026-09-19 — ONE ACCOUNT, ONE CAREER, ONE RESUME
// ===================================================================
// This screen used to be subject-scoped: a household with three performers
// had three resumes, and the URL prefix (lib/subjectScope, now deleted)
// decided which one was showing. Huan's ruling ends that — an account is one
// career — so there is exactly one resume an account can ever have. Every
// read below is simply this tenant's own, and the "performer" record
// (`aa_subject`) this sheet is about is resolved by reading it directly
// rather than being told which one by a scope on the path.
//
// ===================================================================
// W5 — AND THE THREE BLOCKS THAT CAN ONLY BE TYPED
// ===================================================================
// The paragraph above says nothing on this screen is typed twice, and that
// still holds: it is about CREDITS, and there is still no "add a credit"
// here. Training, special skills and awards are the other case. Nothing in
// this app derives them — a class has no engagement, a prize has no project —
// so a person saying so is the only door they have ever had, and until W5
// they did not have that either: `aa_training` / `aa_award` / `aa_skill` sat
// behind the Development registry tab and never reached the page they exist
// for, while the resume above them claimed to be the whole career.
//
// So each block lists, adds, edits and retires through the SAME generic
// record engine every registry row in this app already uses
// (lib/recordEditor's overlay: RecordForm to type it, RecordActions to retire
// it). No bespoke forms, no new server routes, no second copy of the pack's
// field list — the form is rendered from the type's schema, which is why the
// fields on it are the pack's own by construction rather than by care.
//
// ⚑ UNGATED, EVERY TIER (plan of record §2). Typing your own history is not
// a paid feature and there is no requireFeature/planHas on this path. ⚑
// `resume.editor` and the old-resume IMPORT (`resume.import_old`, Free+
// since B7, 2026-09-24, `defa9aed`) are BOTH Free now too — this line used
// to name them as what Working buys; neither still is.
//
// THE ADD DOORS DO NOT LIVE ON THE SHEET. The blocks print; the doors do not,
// exactly as the variant switcher and the Print button do not — see `rz__fill`
// below, and the print rules in resume.css.
//
// ===================================================================
// PRINTING
// ===================================================================
// window.print() over an @media print block, which is what the old actor
// pack landed on and the one part of it worth keeping: no PDF library, no
// download the artifact sandbox would block, and the browser already knows
// how to make a PDF out of a page. What prints is the resume and nothing
// else — no rail, no strip, no variant switcher.
//
// ===================================================================
// W3 — AND THE RESUMES THE PERSON SHAPED, SAVED WITH THEIR OWN FACE
// ===================================================================
// Everything above assembles ONE canonical document, and that is the right
// default and stays the default on every tier. It is not what gets SENT. A
// stage submission wants theatre at the top; a musical wants the singing
// training on the first page; a commercial agent does not want six student
// films. So a person promotes, reorders and turns rows off — and then has to
// do it all again next week, which is the part that makes it not worth doing.
//
// `aa_resume_version` remembers the arrangement: "Theatre 2026", saved, WITH
// ITS OWN HEADSHOT, ready to print again in November. Decision `7c04876f`.
//
// ⚑ WHICH DOORS ARE THE PACK'S FORM AND WHICH ARE OURS, and the line is not
// arbitrary. Naming a version, choosing its headshot, writing a note about it
// and retiring it are all ORDINARY RECORD EDITS — a field and two
// relationships on a pack type — so they go through the same generic editor
// every registry row uses (lib/recordEditor's overlay: RecordForm to type it,
// RecordActions to retire it), exactly as W5's blocks do. No bespoke form, no
// second copy of the pack's fields, and no new server route.
//
// The ARRANGEMENT has no generic form, because there is no such thing as a
// text input for "theatre above film, and not that student short". That is
// the one thing this screen builds itself, and it writes `layout` through the
// ordinary PATCH /records/:id.
//
// ⚑ GATED `resume.editor` (Working+), AND READS ARE NOT. The server refuses
// the write (the pack declares `recordWriteFeatures`; the generic record
// routes enforce it); this screen reads `planHas` so it does not OFFER what
// the tier does not include. A household that lapses keeps every version it
// saved, opens it, and prints it — what it loses is the ability to arrange a
// new one. Content is never held hostage over a plan.
//
// ⚑ THE AUTOMATIC RESUME IS ALWAYS THE FIRST DOCUMENT ON THE STRIP, on every
// tier. A saved version is an addition to this screen, never a replacement
// for it: the free product does not get quietly worse the day the paid one
// exists.
//
// ===================================================================
// THE PROFILE PASS (2026-09-08) — EVERYTHING ABOVE WAS BUILT AND INVISIBLE
// ===================================================================
// Frame m19 of the manual is this screen with no face on it and no sign that
// any of it can be arranged. Every capability in the two headers above was
// already shipping: `aa_profile`'s chosen face, a version's own face,
// `moveSection`, `toggleRow`, `placeRow`, `printableSections`. What none of
// them had was a control that named itself — you could only find them if you
// already knew they were there, which for a household is the same as not
// having them.
//
// So this pass adds NO capability and NO record field. It gives each one a
// surface and a receipt:
//
//   THE SUBJECT BLOCK — the face beside the name, at the head of the
//     document, the way an actor's resume has looked for fifty years. With no
//     face chosen there is a considered slot that says what choosing one
//     does, and that slot is CHROME: it is hidden at print, because an empty
//     rectangle with a sentence in it must never reach a casting director.
//   THE LOOKS — one component (`LookStrip`), used twice: for the household's
//     own face on the automatic resume (every tier — the face is not a paid
//     feature) and for an arrangement's own face inside the arranger (which
//     IS arranging, so Working+). The one that prints is marked in the WORD.
//   THE SHELF — the document strip, with each document's own face on it and
//     the open one named in the word rather than by a coloured border.
//   THE RECEIPTS — every arranging control now answers with a sentence
//     naming what changed ("Training moved above Television"), and the
//     arranger says what is actually on the sheet (`printableSections`,
//     which until now was visible only as the sheet itself).
//   THE PERFORMER DOOR — deleted 2026-09-19. It used to let a parent
//     standing here pick which child's resume to view; an account is one
//     career now, so this screen is always this account's own.
//
// ⚑ A FACE IS TWO WRITES, IN ONE ORDER. `profile_current_headshot` and
// `resume_version_headshot` are both cardinality ONE, and core's records.ts
// THROWS on a second link rather than replacing the first — so choosing a
// face unlinks the old one and then links the new, exactly as
// RecordForm.reconcileLinks does. Anything else fails with the old face
// still on the document.

export interface PerformerResumeProps {
  tenantId: string;
  /** The DEPLOYMENT's product name, read from the pack's entry config — the
   * same answer the tab title and the reviewer's masthead already use, never
   * a literal. `null` until it lands, and a sheet renders no mark rather than
   * a guessed one. */
  productName?: string | null;
}

type LoadState = 'loading' | 'ready' | 'error';

/** THE AUTOMATIC RESUME'S OWN ID ON THE DOCUMENT STRIP. A string rather than
 * null so the strip's chips, their keys and their pressed state all run on
 * one type — a null-or-id union in JSX is where an "everything" chip ends up
 * rendering as blank. */
const AUTOMATIC = 'automatic';

export function PerformerResume({ tenantId, productName = null }: PerformerResumeProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [credits, setCredits] = useState<Credit[]>([]);
  // W5 — the three typed blocks, already shaped. Held beside the credits
  // rather than inside a variant: they are the same on the cast resume, the
  // crew resume and the combined one, because training and skills belong to
  // the person, not to one half of their career.
  const [blocks, setBlocks] = useState<ResumeBlock[]>([]);
  // W3 — the saved arrangements, and the links that carry each one's own
  // headshot.
  const [versions, setVersions] = useState<RecordRow[]>([]);
  const [versionLinks, setVersionLinks] = useState<Record<string, LinkRow[]>>({});
  /** The face the household already chose (aa_profile). What a version prints
   * when it names no headshot of its own — never "no photo". */
  const [defaultHeadshotId, setDefaultHeadshotId] = useState<string | null>(null);
  /** The performer's own aa_profile record and its links — what the household's
   * face is CHOSEN on. Null when there is no profile yet; choosing a face then
   * makes one (the pack's own "one per subject" convention), which is the
   * honest reading of a household that has never opened this door. */
  const [profile, setProfile] = useState<RecordRow | null>(null);
  const [profileLinks, setProfileLinks] = useState<LinkRow[]>([]);
  /** The photographs a face can be chosen from. `looksFrom` decides which
   * media are faces; see lib/resumeProfile.ts on why that is a kind test. */
  const [looks, setLooks] = useState<RecordRow[]>([]);
  /** The performer this account's own career is (`aa_subject`) — read
   * tenant-wide, since an account is one career and there is at most one.
   * Null before the account has claimed itself (ClaimYourself), which is a
   * real state and not a household with nobody chosen. */
  const [subject, setSubject] = useState<RecordRow | null>(null);
  const [plan, setPlan] = useState<PlanApi | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  /** Which document is open: AUTOMATIC, or a version's record id. */
  const [openDoc, setOpenDoc] = useState<string>(AUTOMATIC);
  /** The sentence the last control said back.
   *
   * ⚠ IT LIVES HERE, NOT IN THE CONTROL, AND THE WALK IS WHY. Choosing a face
   * writes and then calls `bumpRecordsVersion()` so the sheet shows the new
   * photograph — which re-runs this screen's read and remounts everything
   * under it. The receipt was state inside the picker, so it was destroyed by
   * the very refresh that proved it true: the walk clicked a look, the link
   * landed on the profile, and the screen said nothing at all. One state, on
   * the component that survives its own reload. */
  const [receipt, setReceipt] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  // ── THE PLAN, READ ON ITS OWN AND FAILING ON ITS OWN ──
  //
  // Deliberately NOT in the content effect below. A tier is not content: if
  // this read fails the resume must still render every credit and every saved
  // version, and `planHas(null, …)` is false — so the arranging doors are
  // simply not offered for a beat. A screen that blanked a career because an
  // entitlement lookup blinked would be punishing the reader for our outage.
  useEffect(() => {
    let cancelled = false;
    void getPlan(tenantId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {
        // Not swallowed into nothing: the state IS the report. No plan means
        // no editor is offered, which is the visible, safe direction.
        if (!cancelled) setPlan(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // ⚑ A REFRESH DOES NOT BLANK THE RESUME. Every read surface in the app
      // reloads off `recordsVersion`, so a face chosen or a row edited used to
      // replace the whole career with "One moment…" for the length of four
      // round trips — a full white-out as the answer to a one-tap change, and
      // the reason the receipt for that change could not be seen. The first
      // load still says it is loading, because then there is genuinely
      // nothing to look at.
      setState((prev) => (prev === 'ready' ? 'ready' : 'loading'));
      setFailure(null);
      try {
        const engagements = await listAllRecordsWithLinks(tenantId, AA.ENGAGEMENT, {
          linkTypes: [LINK.ENGAGEMENT_ROLE],
        });
        // Wide. Roles and projects declare no walk to a performer (see the
        // header) and this account's own aa_subject — there is at most one
        // now — is looked up the same way rather than being told which one
        // by a scope on the path.
        const [roles, projects, subjects] = await Promise.all([
          listAllRecordsWithLinks(tenantId, AA.ROLE, { linkTypes: [LINK.ROLE_PROJECT] }),
          listAllRecordsWithLinks(tenantId, AA.PROJECT, { linkTypes: [] }),
          listAllRecordsWithLinks(tenantId, AA.SUBJECT, { linkTypes: [] }),
        ]);
        // NOT activeOnly: a retired performer's saved sheet still opens, and
        // their name is still their name — a nameless page would be worse
        // than a page for someone who has stepped back.
        const mySubject = subjects.records[0] ?? null;

        // W5 — the typed blocks, this account's own, tenant-wide like
        // everything else here now that there is nothing left to narrow
        // between. Read in the SAME try as the credits, deliberately: a
        // refused read here is a refused read, and a resume that quietly
        // dropped its training block would look exactly like a person who
        // has taken no classes — the failure this screen's error branch
        // exists to refuse.
        const [training, skills, awards] = await Promise.all([
          listAllRecordsByType(tenantId, AA.TRAINING),
          listAllRecordsByType(tenantId, AA.SKILL),
          listAllRecordsByType(tenantId, AA.AWARD),
        ]);
        // W3 — the saved versions and the profile's current headshot. In the
        // SAME try for the SAME reason: a household whose versions failed to
        // load and whose strip therefore showed only "Automatic" would look
        // exactly like a household that has never saved one, and would then
        // be invited to make the version they already have.
        //
        // ⚑ AND THE PHOTOGRAPHS, in the same try again. A looks strip that
        // came back empty because a read failed looks exactly like a
        // household that has never taken a headshot — and would then be
        // shown the honest empty line about a photograph it already has.
        const [savedVersions, profiles, media] = await Promise.all([
          listAllRecordsWithLinks(tenantId, AA.RESUME_VERSION, {
            linkTypes: [LINK.RESUME_VERSION_HEADSHOT],
          }),
          listAllRecordsWithLinks(tenantId, AA.PROFILE, {
            linkTypes: [LINK.PROFILE_CURRENT_HEADSHOT],
          }),
          listAllRecordsWithLinks(tenantId, AA.MEDIA, {}),
        ]);
        if (cancelled) return;

        setSubject(mySubject);

        setBlocks(
          resumeBlocks({
            // Retired means retired everywhere: a class a person archived is
            // off the page, the same rule every registry list holds.
            training: activeOnly(training),
            skills: activeOnly(skills),
            awards: activeOnly(awards),
          })
        );

        const liveVersions = activeOnly(savedVersions.records);
        setVersions(liveVersions);
        setVersionLinks(savedVersions.linksByRecordId);
        // ONE profile per performer by convention (the pack says so
        // plainly), and there is at most one performer now — so the first
        // live one, if any, is this account's own face.
        const liveProfiles = activeOnly(profiles.records);
        const mine = liveProfiles[0] ?? null;
        setProfile(mine);
        setProfileLinks(mine ? profiles.linksByRecordId[mine.id] ?? [] : []);
        setDefaultHeadshotId(
          mine
            ? otherEnd(profiles.linksByRecordId[mine.id] ?? [], mine.id, LINK.PROFILE_CURRENT_HEADSHOT)
            : null
        );
        setLooks(looksFrom(activeOnly(media.records)));

        const rolesById = new Map(activeOnly(roles.records).map((r) => [r.id, r]));
        const projectsById = new Map(activeOnly(projects.records).map((r) => [r.id, r]));
        setCredits(
          assembleCredits({
            engagements: activeOnly(engagements.records),
            rolesById,
            projectsById,
            roleOf: (id) => otherEnd(engagements.linksByRecordId[id] ?? [], id, LINK.ENGAGEMENT_ROLE),
            projectOf: (id) => otherEnd(roles.linksByRecordId[id] ?? [], id, LINK.ROLE_PROJECT),
          })
        );
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setState('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, retryNonce]);

  // The performer this account's own career is — derived from the resolved
  // record, never from a scope on the path. Null before the account has
  // claimed itself; see `subject`'s own state comment.
  const subjectId = subject?.id ?? null;

  if (state === 'loading') {
    return (
      <div class="rz">
        <div class="rz__say">One moment…</div>
      </div>
    );
  }

  if (state === 'error' && failure !== null) {
    // A refused read is never rendered as an empty resume. An actor who sees
    // a blank page has been told they have no credits, which is a different
    // and much worse statement than "this did not load".
    return (
      <div class="rz">
        <div class="rz__say">
          {failure.headline}
          <br />
          <span class="rz__say-soft">{failure.detail}</span>
        </div>
        {/* ⚑ A RETRYABLE FAILURE MUST OFFER THE RETRY. Found while
            photographing this screen for the marketing site: it assembles
            from three reads at once and tripped the rate limiter, which said
            in so many words "the same read will work again shortly" — and
            then gave the reader nothing to press. A sentence promising that
            waiting helps, with no way to try, is worse than the failure. */}
        {failure.retryable ? (
          <button type="button" class="rz__print" onClick={() => setRetryNonce((n) => n + 1)}>
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  const variants = resumeVariants(credits);
  const canEdit = planHas(plan, 'resume.editor');
  // W4 — the switching family's existing resume, read into past credits
  // instead of retyped. `resume.import_old` moved to Free 2026-09-24
  // (decision `defa9aed`) — every tier now grants it — so this reads false
  // only while `plan` is in flight or the read failed, the same rule
  // `canEdit` above follows; it is no longer a tier gate in practice.
  const canImportOld = planHas(plan, 'resume.import_old');

  // ── ⚑ P4 — THE MARK ON A DOCUMENT THAT LEAVES THE HOUSEHOLD (`2f377423`) ──
  //
  // A resume is handed to a casting director, so it is one of the three
  // surfaces Pro's de-branding covers. Below Pro the sheet carries the
  // product's mark; on Pro it carries nothing but the career. That asymmetry
  // IS the feature — the free base carries the brand, the payer buys
  // invisibility — and re-adding the mark for a Pro household "for
  // consistency" would delete something they are paying for.
  //
  // ⚑ THE SCREEN STAYS BRANDED EITHER WAY. The rail, the nav and the buttons
  // are ours and are not on the page that leaves; only the sheet is.
  //
  // ⚠ READ FROM `plan.brand`, NOT FROM THE FEATURE LIST. The server answers
  // the tier and the household's name together (plugins/plan.ts) — a non-null
  // brand means de-branded. Deriving the flag here and the name elsewhere is
  // two facts that drift.
  //
  // ⚠ FAILS TO THE BRANDED SIDE. `plan` is null while the read is in flight
  // and after a read that failed, and both leave the mark ON: being branded
  // is the default experience, where guessing the other way would hand a
  // household a paid feature it has not bought, on a page it then posts.
  const sheetMark = plan?.brand ? null : productName;

  // W3 — everything a saved version could draw on, rebuilt from the LIVE
  // records on every render. BOTH halves of the career, never one variant:
  // "the theatre one" may perfectly well hold two departments and no film.
  const catalogue = catalogueFrom([...castResume(credits), ...crewResume(credits)], blocks);

  if (variants.length === 0 && blocks.length === 0 && versions.length === 0) {
    return (
      <div class="rz">
        {/* A STATEMENT OF FACT, never an instruction and never a prompt. The
            credits half of this page assembles itself out of bookings, so
            there is nothing to ask for there — but the three typed blocks
            below have nowhere else to come from, and their doors stay on the
            page. An empty page with no doors is how the pack's three manual
            types went four months without a screen. */}
        <div class="rz__empty">A resume assembles itself here once a booking is on the books.</div>
        <FillDoors subjectId={subjectId} canImportOld={canImportOld} />
      </div>
    );
  }

  /** Open another document, and DROP the receipt with the one it belonged to.
   * "Training moved below Special skills" standing over a different
   * arrangement is a sentence about a page nobody is looking at. */
  const chooseDoc = (doc: string) => {
    setOpenDoc(doc);
    setReceipt(null);
  };

  // W3 — a version id that no longer names a live version (retired in
  // another tab) falls back to the automatic resume rather than rendering
  // nothing.
  const openVersion = versions.find((v) => v.id === openDoc) ?? null;

  if (openVersion) {
    return (
      <VersionDocument
        key={openVersion.id}
        tenantId={tenantId}
        subjectId={subjectId}
        subject={subject}
        version={openVersion}
        versions={versions}
        versionLinks={versionLinks}
        links={versionLinks[openVersion.id] ?? []}
        defaultHeadshotId={defaultHeadshotId}
        looks={looks}
        catalogue={catalogue}
        canEdit={canEdit}
        sheetMark={sheetMark}
        receipt={receipt}
        onReceipt={setReceipt}
        onChooseDoc={chooseDoc}
      />
    );
  }

  // W5 — NULL IS NOW A REAL STATE HERE. Before the typed blocks, reaching
  // this line meant there was at least one variant; a resume that is nothing
  // but a training block has none, and the page is still a page.
  const active: ResumeVariant | null =
    variants.find((v) => v.key === chosen) ?? variants[0] ?? null;
  const groups = active?.groups ?? [];
  const isEverything = active?.key === 'everything';

  return (
    <div class="rz">
      <DocShelf
        tenantId={tenantId}
        versions={versions}
        versionLinks={versionLinks}
        defaultHeadshotId={defaultHeadshotId}
        openDoc={openDoc}
        canEdit={canEdit}
        onChoose={chooseDoc}
      />

      {/* The switcher and the print control are NOT part of the resume — the
          print stylesheet hides this row entirely. */}
      <div class="rz__bar">
        {variants.length > 1 ? (
          <div class="rz__variants">
            {variants.map((v) => (
              <button
                type="button"
                key={v.key}
                class={`rz__variant${v.key === active?.key ? ' rz__variant--on' : ''}`}
                aria-pressed={v.key === active?.key}
                onClick={() => setChosen(v.key)}
              >
                {v.label}
              </button>
            ))}
          </div>
        ) : active ? (
          <span class="rz__only">{active.label}</span>
        ) : (
          // W5 — no credits yet, but something typed. NOTHING is said where
          // the variant name would go: a career with no bookings has no
          // variant, and naming one ("Cast") over a page holding only a
          // training block would be the page claiming a credit it has not
          // got. The Print button stays — a training-and-skills sheet is a
          // real thing a young performer sends.
          null
        )}
        {/* W3 — THE ONE DOOR THAT MAKES A VERSION, and it stands on the
            AUTOMATIC resume because that is what a version is made FROM: the
            layout you are looking at, kept. Offered only to a tier that
            includes the editor — a button that always errors reads as broken
            rather than as an upsell (plugins/plan.ts's own words). */}
        {canEdit ? (
          <button
            type="button"
            class="rz__save-as"
            onClick={() =>
              openRecordEditor({
                mode: 'create',
                typeKey: AA.RESUME_VERSION,
                // The arrangement travels INTO the pack's own create form as
                // a seeded field, so the person names it and the layout is
                // already there. RecordForm renders a `json` field read-only
                // and carries it through untouched (its own comment says why).
                prefillFields: { layout: layoutFromCatalogue(catalogue) },
                // ⚑ KEYED BY THE PACK'S RELATIONSHIP KEY — a wrong key here is
                // DISCARDED with no error and the form opens unlinked (the A4
                // prefill defect, packLinkKeys.test.ts).
                ...(subjectId ? { prefillRels: { [LINK.RESUME_VERSION_SUBJECT]: [subjectId] } } : {}),
              })
            }
          >
            Keep this arrangement
          </button>
        ) : null}
        {/* Offered only over a document with something on it. With no
            credits and no typed blocks (reachable once a saved version
            exists) the sheet is bare, and the app-wide print reveal shows
            only `.print-resume` — so Print here would hand over a blank
            page. The version screen holds the same rule for the layout it
            cannot read. */}
        {groups.length > 0 || blocks.length > 0 ? (
          <button type="button" class="rz__print" onClick={() => window.print()}>
            Print
          </button>
        ) : null}
      </div>

      {/* ⚑ `print-resume` IS NOT DECORATION. Materials.css ships an app-wide
          print rule that hides `body *` and reveals only `.print-resume` —
          and CSS is global once bundled, so that rule is live on this screen
          whether or not the old pack is booted. Without this class the print
          view is a blank page: the shared reveal would hide this sheet along
          with everything else. */}
      <div class="rz__sheet print-resume">
        {/* The profile's current headshot, printed the way a saved version
            prints its own — the automatic resume is as much the document
            that leaves as any arrangement of it. Null in the household,
            where nobody has said whose face this is. */}
        <SubjectBlock
          tenantId={tenantId}
          subject={subject}
          headshotId={defaultHeadshotId}
          alt={subject ? `${subject.display_name} headshot` : 'Headshot'}
          offerSlot={subjectId !== null}
        />
        {groups.map((group, i) => (
          <section class="rz__group" key={`${active?.key}-${group.side}-${group.group}`}>
            {/* ⚑ ONLY ON "EVERYTHING", AND ONLY AT THE TURN. On a cast-only
                resume a "Cast" banner over the one thing on the page is
                furniture. On the combined one it is the difference between a
                career with two halves and a list of five departments — which
                is what the first screenshot of this screen looked like, with
                FILM sitting directly above LIGHTING and nothing saying they
                were different kinds of work. */}
            {isEverything && group.side !== groups[i - 1]?.side ? (
              <div class="rz__side">{group.side === 'cast' ? 'Cast' : 'Crew'}</div>
            ) : null}
            <h2 class="rz__group-k">{group.group}</h2>
            <table class="rz__table">
              <tbody>
                {group.credits.map((c) => (
                  <tr class="rz__row" key={c.engagementId}>
                    <td class="rz__production">{c.production ?? '—'}</td>
                    <td class="rz__role">{c.role}</td>
                    <td class="rz__meta">{creditLine(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}

        {/* W5 — the typed blocks, AFTER the credits and in the printed
            order (training · special skills · awards; see lib/resume.ts's
            resumeBlocks for why that order and why the awards are their own
            block rather than a line inside training). An empty block is not
            here at all — resumeBlocks drops it — so there is never a rule
            with white space under it on the page. */}
        {blocks.map((block) => (
          <section class="rz__group" key={block.key}>
            <h2 class="rz__group-k">{block.label}</h2>
            <table class="rz__table">
              <tbody>
                {block.rows.map((r) => (
                  <tr class="rz__row" key={r.id}>
                    <td class="rz__blk-k">{r.lead ?? ''}</td>
                    <td class="rz__blk-v">{r.middle ?? ''}</td>
                    <td class="rz__meta">{r.trail ?? ''}</td>
                    {/* ⚑ THE EDIT DOOR RIDES INSIDE THE SHEET AND IS HIDDEN
                        AT PRINT — the same shape the frozen actor pack's own
                        resume used for its remove control (Materials.css's
                        `.print-resume .resume__x { display: none }`). It has
                        to sit on the row: a row is a record, and a person
                        fixing a misspelled school name looks at the
                        misspelling, not at a second list of the same lines.
                        Retiring one happens behind this same door, in
                        RecordActions — nothing in this app deletes (Huan,
                        2026-08-12). */}
                    <td class="rz__blk-x">
                      <button
                        type="button"
                        class="rz__edit"
                        aria-label={`Edit ${r.lead ?? block.label}`}
                        onClick={() => openRecordEditor({ mode: 'edit', typeKey: block.typeKey, id: r.id })}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}

        <SheetMark productName={sheetMark} />
      </div>

      {/* ⚑ THE HOUSEHOLD'S FACE, ON EVERY TIER. `resume.editor` gates
          ARRANGING; a photograph is not an arrangement, and the free product
          gets the face and the body (plan of record §2). Offered only inside
          one performer — in the household nobody has said whose face this
          would be, which is the same rule the sheet's own head follows. */}
      {subjectId !== null ? (
        <ProfileLooks
          tenantId={tenantId}
          subjectId={subjectId}
          subject={subject}
          profile={profile}
          profileLinks={profileLinks}
          looks={looks}
          chosenId={defaultHeadshotId}
          receipt={receipt}
          onReceipt={setReceipt}
        />
      ) : null}

      <FillDoors subjectId={subjectId} canImportOld={canImportOld} />
    </div>
  );
}

/**
 * P4 — THE PRODUCT'S MARK, AT THE FOOT OF THE SHEET (`2f377423`).
 *
 * ⚑ INSIDE `.print-resume`, and that is the whole point of it. Everything else
 * this screen renders is scaffolding that must not appear on the thing a
 * household hands over; this is the one thing that is deliberately ON the
 * document — below Pro, where the free base carries the brand.
 *
 * ⚑ IT IS ON SCREEN TOO, not conjured at print time. A person should see the
 * page they are about to print; a mark that appeared only on paper would be a
 * surprise, and surprise is what this product demotes everywhere else.
 *
 * ⚠ `null` RENDERS NOTHING AT ALL — not an empty rule, not reserved space. On
 * a Pro sheet there must be no trace that a mark was ever considered.
 */
function SheetMark({ productName }: { productName: string | null }) {
  if (!productName) return null;
  return <div class="rz__mark">Made with {productName}</div>;
}

/**
 * THE HEAD OF THE SHEET — whose career this is.
 *
 * The printed page travels with no app around it, and a casting director
 * holding a resume with no name on it is holding scrap. The name is the
 * subject record's own display name — the pack's
 * `{professional_name|legal_name}` chain, the credited name when there is
 * one — never re-derived from fields here.
 *
 * The line under it holds only facts that exist: union affiliation the way
 * the industry writes it, then the representation the household recorded.
 * An absent fact renders nothing — no empty labels, no "Union:". The union
 * NUMBER is deliberately not printed: a membership number is a credential,
 * not a line for strangers.
 *
 * ⚑ INSIDE `.print-resume`, like the mark — this is ON the document, not
 * chrome around it. And null renders nothing at all: standing in the
 * household there is no single performer, the same rule the headshot
 * already follows.
 */
/**
 * THE SUBJECT OF THE DOCUMENT — the face and the name, together.
 *
 * ⚑ THE MARKUP IS UNCHANGED FOR PAPER, AND THAT IS DELIBERATE. `.rz__shot` is
 * still a float and still the first thing in the block, so on the printed
 * page the credits run under and around a 9rem photograph exactly as they
 * have since A10 — the shape an actor's resume has had for fifty years, and
 * the reason resume.css calls the float a decision rather than a layout
 * accident. The wrapper is a plain block that does NOT establish a formatting
 * context, so the float still escapes it and `.rz__sheet::after` still
 * contains it. What the wrapper buys is the SCREEN: a portrait beside the
 * name on a desktop, above it on a phone, which a bare float cannot express.
 *
 * ⚑ THE EMPTY SLOT IS CHROME, NOT DOCUMENT. With no face chosen there is a
 * considered slot saying what choosing one does — and it is hidden at print
 * (resume.css names `.rz__face-none` in its @media print block beside
 * `.rz__edit`). An empty rectangle with a sentence in it must never reach a
 * casting director.
 *
 * ⚠ AND IT IS NOT `.rz__shot`. A household with no chosen face renders no
 * `.rz__shot` at all — a pin PerformerResumeVersions.test.ts has held since
 * W3, because a broken photograph on a resume is worse than none.
 *
 * `offerSlot` is false in the household, where nobody has said whose face
 * this would be: an empty slot there would be asking a question that has no
 * single answer.
 */
function SubjectBlock({
  tenantId,
  subject,
  headshotId,
  alt,
  offerSlot,
}: {
  tenantId: string;
  subject: RecordRow | null;
  headshotId: string | null;
  alt: string;
  offerSlot: boolean;
}) {
  if (!subject && !headshotId) return null;
  return (
    <div class="rz__subject">
      {headshotId ? (
        <div class="rz__shot">
          <RecordImage
            tenantId={tenantId}
            recordId={headshotId}
            fieldName="original_file_ref"
            alt={alt}
            className="rz__shot-img"
          />
        </div>
      ) : offerSlot ? (
        <div class="rz__face-none">
          {/* Reassurance about what the SYSTEM does with the choice, never
              "tap to choose": the strip below is a row of photographs with
              one marked "printing", and a surface that has to say how it
              works in prose has not taught itself. */}
          <span class="rz__face-none-say">The look you choose here prints on every arrangement.</span>
        </div>
      ) : null}
      <SheetHead subject={subject} />
    </div>
  );
}

function SheetHead({ subject }: { subject: RecordRow | null }) {
  if (!subject) return null;
  const credentials = [unionAffiliation(subject.fields), textField(subject.fields, 'representation')]
    .filter((part): part is string => part !== null)
    .join(' · ');
  return (
    <header class="rz__head">
      <h1 class="rz__name">{subject.display_name}</h1>
      {credentials ? <div class="rz__cred">{credentials}</div> : null}
    </header>
  );
}

/** A non-member standing is spelled out beside the union's name — "ACTRA"
 * alone is a membership claim, and this page is handed to people who know
 * the difference. `member` needs no word: the name IS the claim. */
const UNION_STANDING: Record<string, string> = {
  eligible: 'Eligible',
  apprentice: 'Apprentice',
  financial_core: 'Financial Core',
};

function unionAffiliation(fields: Record<string, unknown>): string | null {
  const status = textField(fields, 'union_status');
  // The status wins over a lingering union name: "ACTRA · Non-union" would
  // contradict itself on the one page that must not.
  if (status === 'non_union') return 'Non-union';
  const name = textField(fields, 'union_name');
  if (!name) return null;
  const standing = status ? UNION_STANDING[status] ?? null : null;
  return standing ? `${name} ${standing}` : name;
}

function textField(fields: Record<string, unknown>, name: string): string | null {
  const value = fields[name];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * W3 — THE DOCUMENT STRIP: the automatic resume, then every saved version.
 *
 * ⚑ RENDERED ON EVERY TIER once a version exists, and NOT rendered when only
 * the automatic resume does. A household with one document does not need a
 * chooser for it — that is the same rule the variant switcher one function up
 * already follows, and the reason a free household's resume screen looks
 * exactly as it did before this package landed.
 *
 * ⚑ THE STRIP IS A READ. It appears for a lapsed household too, holding every
 * version they ever saved, and each one opens and prints. `resume.editor`
 * gates ARRANGING, never reading — content is not held hostage over a plan
 * (framework §5).
 */
function DocShelf({
  tenantId,
  versions,
  versionLinks,
  defaultHeadshotId,
  openDoc,
  canEdit,
  onChoose,
}: {
  tenantId: string;
  versions: RecordRow[];
  versionLinks: Record<string, LinkRow[]>;
  /** What a version prints when it names no face of its own — so a thumb on
   * this shelf and the photograph on the sheet can never disagree. */
  defaultHeadshotId: string | null;
  openDoc: string;
  canEdit: boolean;
  onChoose: (doc: string) => void;
}) {
  if (versions.length === 0) {
    // ⚑ BELOW TIER, ONE LINE WHERE THE SHELF WOULD BE. A household that has
    // never been able to arrange one has no shelf and no explanation for the
    // absence — which reads as a product with one resume in it. One quiet
    // sentence, once, and never a paragraph in prime space.
    //
    // A household that CAN arrange gets nothing here at all: the door is
    // "Keep this arrangement" one row down, and a chooser for a single
    // document is furniture.
    if (canEdit) return null;
    return (
      <div class="rz__docs">
        <span class="rz__lapsed">
          On Working, an arrangement is kept — a version per submission, each with its own face.
        </span>
      </div>
    );
  }
  const card = (id: string, label: string, faceId: string | null) => {
    const open = id === openDoc;
    return (
      <button
        type="button"
        key={id}
        class={`rz__doc${open ? ' rz__doc--on' : ''}`}
        aria-pressed={open}
        onClick={() => onChoose(id)}
      >
        {/* THE FACE THAT GOES WITH THIS ONE, which is half of what a saved
            version is for: a theatre submission and a commercial submission
            are two different photographs, and a shelf of identical text
            chips is exactly the surface that hid that. */}
        {faceId ? (
          <span class="rz__doc-face">
            <RecordImage
              tenantId={tenantId}
              recordId={faceId}
              fieldName="original_file_ref"
              alt=""
              className="rz__doc-face-img"
            />
          </span>
        ) : null}
        <span class="rz__doc-name">{label}</span>
        {/* ⚑ THE WORD, NOT THE BORDER. Which document is open was said only
            by an accent border and `aria-pressed` — a state carried by a hue
            alone, which is the one thing this product's own rules forbid. */}
        {open ? <span class="rz__doc-now">showing</span> : null}
      </button>
    );
  };
  return (
    <div class="rz__docs">
      {card(AUTOMATIC, 'Automatic', defaultHeadshotId)}
      {versions.map((v) =>
        card(
          v.id,
          versionName(v),
          otherEnd(versionLinks[v.id] ?? [], v.id, LINK.RESUME_VERSION_HEADSHOT) ?? defaultHeadshotId
        )
      )}
    </div>
  );
}

// 2026-09-19 — THE PERFORMER DOOR IS DELETED. It used to let a parent
// standing on the household's resume screen pick which child's career to
// view, by writing a `/p/:id` scope into the hash (lib/subjectScope, also
// deleted). An account is one career now, so there is nobody left to choose
// between: this screen is always this account's own resume.

/**
 * THE LOOKS — one strip, used for both faces this product has.
 *
 * ⚑ ONE COMPONENT, TWO DOCUMENTS. The household's face (`aa_profile`) and an
 * arrangement's own (`aa_resume_version`) are the same act on two different
 * records, and two copies of a picker is how they end up disagreeing about
 * what "printing" means. The caller supplies what is chosen and what to do
 * about a tap; this holds the shape and the word.
 *
 * ⚑ THE PRINTING ONE IS MARKED IN THE WORD. `aria-pressed` and a ring are
 * both invisible to somebody scanning a row of photographs — and a state
 * said in colour alone is the one thing this product's rules forbid outright.
 *
 * ⚑ CHOOSING IS ONE TAP AND ANSWERS WITH A RECEIPT NAMING THE LOOK. Not
 * "Saved": "Stage look prints on Theatre 2026" is a sentence a person can
 * check against the page they are looking at.
 *
 * ⚠ THE CHOSEN ONE CAN ALSO BE UNCHOSEN, and on a version that is not the
 * same as "no photo" — it falls back to the household's face, which is what
 * the receipt then says. On the profile itself it really is no photograph,
 * and the sheet's honest empty slot comes back.
 */
function LookStrip({
  tenantId,
  looks,
  chosenId,
  busyId,
  onChoose,
}: {
  tenantId: string;
  looks: RecordRow[];
  chosenId: string | null;
  busyId: string | null;
  /** `null` means "take the face off this document". */
  onChoose: (lookId: string | null) => void;
}) {
  return (
    <div class="rz__looks-row">
      {looks.map((m) => {
        const on = m.id === chosenId;
        const name = lookName(m);
        return (
          <button
            type="button"
            key={m.id}
            class={`rz__look${on ? ' rz__look--on' : ''}`}
            aria-pressed={on}
            disabled={busyId !== null}
            onClick={() => onChoose(on ? null : m.id)}
          >
            <span class="rz__look-art">
              <RecordImage
                tenantId={tenantId}
                recordId={m.id}
                fieldName="original_file_ref"
                alt={name}
                className="rz__look-img"
              />
            </span>
            <span class="rz__look-name">{name}</span>
            {on ? <span class="rz__look-now">printing</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * THE HOUSEHOLD'S FACE, on the automatic resume.
 *
 * ⚑ UNGATED, EVERY TIER (plan of record §2, and the same rule W5's typed
 * blocks follow). What Working buys is the ARRANGEMENT. Choosing which
 * photograph represents you is not an arrangement, and holding a face hostage
 * over a plan is the thing this product does not do.
 *
 * ⚑ A HOUSEHOLD WITH NO PROFILE RECORD GETS ONE, ON THE FIRST CHOICE. The
 * pack's `aa_profile` is one per performer by convention, and a family that
 * has never opened the registry has none — which would make this door dead on
 * exactly the households it exists for. Created through the ordinary record
 * routes, pre-linked to the performer, and named in the receipt like any
 * other write.
 */
function ProfileLooks({
  tenantId,
  subjectId,
  subject,
  profile,
  profileLinks,
  looks,
  chosenId,
  receipt,
  onReceipt,
}: {
  tenantId: string;
  subjectId: string;
  subject: RecordRow | null;
  profile: RecordRow | null;
  profileLinks: LinkRow[];
  looks: RecordRow[];
  chosenId: string | null;
  /** Held by the screen, not here — a face write refreshes this component out
   * of existence, and a receipt destroyed by the refresh that proved it true
   * is the defect the walk found. */
  receipt: string | null;
  onReceipt: (say: string | null) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const whose = subject ? `${subject.display_name}’s resume` : 'this resume';

  /** ⚑ THE FILE IS THE ACT (2026-09-16, the Day 3–4 walk, item 5). See
   * `uploadHeadshot`'s own block in lib/api.ts for what this replaced and why
   * a generic create form could not be it. Bytes first, then the record that
   * carries their ref — so there is no state in which a look exists without a
   * photograph, which is the state the walk found twice. */
  async function addLook(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setAdding(true);
    setError(null);
    onReceipt(null);
    try {
      await uploadHeadshot(tenantId, subjectId, file);
      // Named, because a receipt says what became of the thing. It is a look
      // now; whether it PRINTS is the separate choice the strip offers.
      onReceipt(`${file.name} is filed to ${subject ? subject.display_name : 'this performer'} as a look`);
      bumpRecordsVersion();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That photograph did not upload, and nothing was filed.');
    } finally {
      setAdding(false);
      // Or the same file cannot be chosen twice after a failure.
      input.value = '';
    }
  }

  async function choose(lookId: string | null) {
    setBusy(lookId ?? 'clear');
    setError(null);
    onReceipt(null);
    try {
      let target = profile;
      let links = profileLinks;
      if (!target) {
        target = await createRecord(tenantId, AA.PROFILE, {});
        await linkRecord(tenantId, target.id, LINK.PROFILE_SUBJECT, subjectId);
        links = [];
      }
      await setLookOn(tenantId, target.id, LINK.PROFILE_CURRENT_HEADSHOT, links, lookId);
      const chosen = lookId === null ? null : looks.find((m) => m.id === lookId) ?? null;
      onReceipt(chosen ? faceReceipt(lookName(chosen), whose) : `${whose} prints no photograph`);
      bumpRecordsVersion();
    } catch (err) {
      // ⚠ LOUD, AND THE SERVER'S OWN SENTENCE. A face that silently stayed as
      // it was is the failure the whole receipt idea exists to end.
      setError(err instanceof Error ? err.message : 'That look stayed as it was.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div class="rz__looks">
      <div class="rz__looks-head">
        <span class="rz__arr-eyebrow">Looks</span>
        {/* ⚑ THE PICKER IS THE BUTTON (2026-09-16, the Day 3–4 walk, item 5).
            This was the pack's generic create form for `aa_media`, with the
            kind and the performer pre-filled — and because that type's file
            field is optional, Save wrote a headshot record with no photograph
            in it, which `looksFrom` then filtered out of this very strip. The
            self-tape door has never had that state and this one now cannot
            either: no file, no record. */}
        <label class={adding ? 'rz__looks-add rz__looks-add--busy' : 'rz__looks-add'}>
          {adding ? 'Uploading…' : 'Add a headshot'}
          <input
            type="file"
            accept="image/*"
            class="rz__looks-file"
            disabled={adding}
            onChange={(e) => void addLook(e)}
          />
        </label>
      </div>
      {looks.length === 0 ? (
        // A statement of what the system does with a photograph, never an
        // instruction to go and file one.
        <div class="rz__looks-empty">A photograph filed to this performer becomes a look here.</div>
      ) : (
        <LookStrip
          tenantId={tenantId}
          looks={looks}
          chosenId={chosenId}
          busyId={busy}
          onChoose={(id) => void choose(id)}
        />
      )}
      {receipt ? (
        <div class="rz__receipt" role="status">
          {receipt}
        </div>
      ) : null}
      {error ? (
        <div class="rz__arr-error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

/**
 * ONE FACE ONTO ONE DOCUMENT — unlink, then link, in that order.
 *
 * ⚠ THE ORDER IS NOT A PREFERENCE. Both face relationships are cardinality
 * ONE, and core's records.ts REFUSES the second link (it throws on
 * `links_cardinality_one_idx`) rather than replacing the first. A fresh link
 * over an existing one therefore fails with the old face still on the
 * document — a change that reports an error and leaves the page looking
 * exactly as it did. This is RecordForm.reconcileLinks' own sequence, for the
 * same reason.
 */
async function setLookOn(
  tenantId: string,
  recordId: string,
  relationshipKey: string,
  links: LinkRow[],
  lookId: string | null
): Promise<void> {
  for (const l of links) {
    if (l.link_type !== relationshipKey) continue;
    if (l.src_id !== recordId && l.dst_id !== recordId) continue;
    await unlinkRecord(tenantId, recordId, l.id);
  }
  if (lookId !== null) await linkRecord(tenantId, recordId, relationshipKey, lookId);
}

/**
 * W3 — ONE SAVED VERSION: the sheet it prints, and the panel that arranges it.
 *
 * ⚑ ITS OWN COMPONENT, KEYED BY THE VERSION'S ID (see the `key` at the call
 * site). Switching documents therefore REMOUNTS this, which throws away any
 * half-made arrangement rather than carrying it onto a different resume —
 * the one bug a shared draft state would guarantee, and the one nobody would
 * notice until a theatre resume printed the commercial one's order.
 */
function VersionDocument({
  tenantId,
  subjectId,
  subject,
  version,
  versions,
  versionLinks,
  links,
  defaultHeadshotId,
  looks,
  catalogue,
  canEdit,
  sheetMark,
  receipt,
  onReceipt,
  onChooseDoc,
}: {
  tenantId: string;
  subjectId: string | null;
  /** The performer whose sheet this is. A saved version leaves the household
   * exactly as the automatic one does, and the two must not disagree about
   * whose name is at the head of it. */
  subject: RecordRow | null;
  version: RecordRow;
  versions: RecordRow[];
  /** Every version's links, so the shelf can carry each one's own face. */
  versionLinks: Record<string, LinkRow[]>;
  links: LinkRow[];
  defaultHeadshotId: string | null;
  /** The photographs a face can be chosen from — read once by the screen and
   * handed down, never re-read per document. */
  looks: RecordRow[];
  catalogue: ResumeCatalogue;
  canEdit: boolean;
  /** P4 — the product's mark, or null on a de-branded (Pro) household. A
   * SAVED version is as much the document that leaves as the automatic one,
   * and the two must not disagree about what is printed on it. */
  sheetMark: string | null;
  /** See ProfileLooks' own note: the screen owns this, because saving a
   * layout refreshes this component out of existence. */
  receipt: string | null;
  onReceipt: (say: string | null) => void;
  onChooseDoc: (doc: string) => void;
}) {
  const [draft, setDraft] = useState<ResumeVersionLayout | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** ⚑ THE ARRANGER IS A QUIET TOGGLE, NOT A SECOND SCREEN. Open by default —
   * the whole finding behind this pass is that arranging was invisible — and
   * foldable, because a person who came to print does not want the workshop
   * under the document. */
  const [arranging, setArranging] = useState(true);
  const [faceBusy, setFaceBusy] = useState<string | null>(null);

  const stored = parseLayout(version.fields.layout);
  const layout = draft ?? stored.layout;
  const resolved = resolveVersion(layout, catalogue);
  const printable = printableSections(resolved);

  // ⚑ ABSENT MEANS THE PROFILE'S CURRENT HEADSHOT, never "no photo". A
  // version saved before anyone thought about the picture prints the face the
  // household already chose.
  const headshotId = otherEnd(links, version.id, LINK.RESUME_VERSION_HEADSHOT) ?? defaultHeadshotId;

  /** This arrangement's own face, chosen here rather than three taps deep in
   * the record form. ARRANGING, so it lives inside the arranger and behind
   * `resume.editor` — unlike the household's face, which is free. */
  async function chooseFace(lookId: string | null) {
    setFaceBusy(lookId ?? 'clear');
    setSaveError(null);
    onReceipt(null);
    try {
      await setLookOn(tenantId, version.id, LINK.RESUME_VERSION_HEADSHOT, links, lookId);
      const chosen = lookId === null ? null : looks.find((m) => m.id === lookId) ?? null;
      onReceipt(
        chosen
          ? faceReceipt(lookName(chosen), versionName(version))
          : faceClearedReceipt(versionName(version))
      );
      bumpRecordsVersion();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'That look stayed as it was.');
    } finally {
      setFaceBusy(null);
    }
  }

  async function save(next: ResumeVersionLayout) {
    setSaving(true);
    setSaveError(null);
    try {
      await updateRecord(tenantId, version.id, { layout: next });
      setDraft(null);
      onReceipt(savedReceipt(versionName(version)));
      // Every read surface in the app reloads off this counter, including the
      // effect that fed us this version — so the saved layout comes back from
      // the server rather than being assumed from the draft we just sent.
      bumpRecordsVersion();
    } catch (err) {
      // ⚠ LOUD, AND THE DRAFT SURVIVES. A refused save that also discarded
      // the arrangement would lose the person's work twice. The server's own
      // sentence is shown: a tier refusal says which plan and that nothing
      // already saved was touched, which is more use than "could not save".
      setSaveError(err instanceof Error ? err.message : 'That did not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="rz">
      <DocShelf
        tenantId={tenantId}
        versions={versions}
        versionLinks={versionLinks}
        defaultHeadshotId={defaultHeadshotId}
        openDoc={version.id}
        canEdit={canEdit}
        onChoose={onChooseDoc}
      />

      {/* THE NOTE THE PERSON WROTE ABOUT THIS ARRANGEMENT — "the one I send
          to Soulpepper". It lives on the record and printed nowhere, which
          made it a field nobody ever saw again after typing it. OUTSIDE the
          sheet: a resume is the one page that goes to a stranger, and the
          pack says plainly this is never printed. */}
      <VersionNote version={version} />

      <div class="rz__bar">
        {canEdit ? (
          <>
            <button
              type="button"
              class="rz__variant"
              onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.RESUME_VERSION, id: version.id })}
            >
              Name, headshot &amp; retire
            </button>
            <button
              type="button"
              class="rz__variant"
              onClick={() =>
                openRecordEditor({
                  mode: 'create',
                  typeKey: AA.RESUME_VERSION,
                  // A duplicate is THIS arrangement under a new name — the
                  // whole reason to duplicate rather than start over. The
                  // headshot rides along too: "the same, but for musicals"
                  // usually means the same face.
                  prefillFields: { layout: layout ?? { v: 1, sections: [] } },
                  ...(subjectId || headshotId
                    ? {
                        prefillRels: {
                          ...(subjectId ? { [LINK.RESUME_VERSION_SUBJECT]: [subjectId] } : {}),
                          ...(headshotId ? { [LINK.RESUME_VERSION_HEADSHOT]: [headshotId] } : {}),
                        },
                      }
                    : {}),
                })
              }
            >
              Duplicate
            </button>
          </>
        ) : (
          // ⚑ THE LAPSE BANNER (framework §5: soft + loud). Short, honest, and
          // standing where the arranging doors were — so the absence is
          // explained rather than merely noticed. It says what is still true
          // FIRST, because the question a person actually has is "have I lost
          // it".
          <span class="rz__lapsed">This one is yours to print. Arranging needs Working.</span>
        )}
        {/* No sheet renders for a layout this client cannot read, and the
            app-wide print reveal shows only `.print-resume` — Print over
            that branch puts a blank page in the tray. The control leaves
            with the document it acts on. */}
        {canEdit && stored.fault !== 'unreadable' ? (
          <button
            type="button"
            class="rz__variant"
            aria-pressed={arranging}
            aria-expanded={arranging}
            onClick={() => setArranging((v) => !v)}
          >
            {arranging ? 'Hide arranging' : 'Arrange'}
          </button>
        ) : null}
        {stored.fault === 'unreadable' ? null : (
          <button type="button" class="rz__print" onClick={() => window.print()}>
            Print
          </button>
        )}
      </div>

      {stored.fault === 'unreadable' ? (
        // ⚠ NEVER HALF-RENDERED. A layout this client cannot read is refused
        // out loud rather than resolved as far as it parses — a partly-applied
        // arrangement prints a document that is not the one its owner saved,
        // on the page they hand to a stranger.
        <div class="rz__say">
          This version was arranged by a newer version of the app.
          <br />
          <span class="rz__say-soft">Nothing is lost — reload, and it will open.</span>
        </div>
      ) : (
        <div class="rz__sheet print-resume">
          <SubjectBlock
            tenantId={tenantId}
            subject={subject}
            headshotId={headshotId}
            alt={`${versionName(version)} headshot`}
            offerSlot={subjectId !== null}
          />
          {printable.length === 0 ? (
            // A version whose every row was turned off, or whose records have
            // all gone. Said plainly rather than printed as a blank sheet.
            <div class="rz__empty">Nothing is on this version yet.</div>
          ) : (
            printable.map((section) => (
              <section class="rz__group" key={section.id}>
                <h2 class="rz__group-k">{section.label}</h2>
                <table class="rz__table">
                  <tbody>
                    {section.rows.map((row) => (
                      <tr class="rz__row" key={row.id}>
                        {/* The SAME three cells the automatic sheet prints,
                            reused rather than re-derived: the two documents
                            must not typeset differently, and the print rules
                            already name these classes. */}
                        <td class="rz__production">{row.lead}</td>
                        <td class="rz__role">{row.middle}</td>
                        <td class="rz__meta">{row.trail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))
          )}

          <SheetMark productName={sheetMark} />
        </div>
      )}

      {canEdit && stored.fault !== 'unreadable' && arranging ? (
        <VersionArranger
          tenantId={tenantId}
          resolved={resolved}
          printable={printable}
          layout={layout}
          catalogue={catalogue}
          looks={looks}
          faceId={otherEnd(links, version.id, LINK.RESUME_VERSION_HEADSHOT)}
          faceBusy={faceBusy}
          dirty={draft !== null}
          saving={saving}
          error={saveError}
          receipt={receipt}
          onChooseFace={(id) => void chooseFace(id)}
          onChange={setDraft}
          onReceipt={onReceipt}
          onSave={() => void save(draft!)}
          onDiscard={() => {
            setDraft(null);
            setSaveError(null);
            onReceipt(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * W3 — THE ARRANGER. Section order, per-row include/exclude, and the tray of
 * things this version has never been told about.
 *
 * ⚑ OUTSIDE `.print-resume`, like W5's add doors and for the same reason:
 * everything that helped somebody build the document is scaffolding that must
 * not appear on the thing they hand over.
 *
 * ⚑ UP/DOWN BUTTONS, NOT DRAG. A drag library is a dependency, a touch
 * problem and an accessibility problem for a control that moves five things.
 * Two buttons work with a thumb, with a keyboard and with a screen reader,
 * and the phone is the form factor.
 *
 * ⚑ EVERY EDIT IS LOCAL UNTIL SAVE. The person pushes six things around and
 * presses Save once; a save that fails leaves the arrangement on screen
 * rather than half-applied on the server.
 */
function VersionArranger({
  tenantId,
  resolved,
  printable,
  layout,
  catalogue,
  looks,
  faceId,
  faceBusy,
  dirty,
  saving,
  error,
  receipt,
  onChooseFace,
  onChange,
  onReceipt,
  onSave,
  onDiscard,
}: {
  tenantId: string;
  resolved: ReturnType<typeof resolveVersion>;
  /** What actually reaches paper — `printableSections`, which until this pass
   * was visible only as the sheet itself. */
  printable: ResolvedSection[];
  layout: ResumeVersionLayout | null;
  catalogue: ResumeCatalogue;
  looks: RecordRow[];
  /** THIS version's own face, or null when it prints the household's. */
  faceId: string | null;
  faceBusy: string | null;
  dirty: boolean;
  saving: boolean;
  error: string | null;
  receipt: string | null;
  onChooseFace: (lookId: string | null) => void;
  onChange: (next: ResumeVersionLayout) => void;
  onReceipt: (say: string | null) => void;
  onSave: () => void;
  onDiscard: () => void;
}) {
  // A version whose `layout` never landed (see the pack's note on why the
  // field is optional) arranges from an empty one rather than refusing to
  // open — and the tray below then holds the entire career, which is exactly
  // what "nothing has been placed" looks like.
  const base: ResumeVersionLayout = layout ?? { v: 1, sections: [] };

  /** ⚠ ONLY REPORT A REAL CHANGE. Every edit helper returns the SAME object
   * when it would have been a no-op (a move off the end), and handing that
   * back would still mark the arrangement dirty — a Save button standing over
   * a document nobody changed, which then writes a revision that means
   * nothing. */
  /** The section headings AS THEY READ ON SCREEN — what a move's receipt is
   * named against. See `movedReceipt` on why the stored order will not do. */
  const visibleOrder = resolved.sections.map((s) => s.label);

  const apply = (next: ResumeVersionLayout, say: string | null) => {
    if (next === base) return;
    onChange(next);
    // ⚑ THE RECEIPT IS SET ONLY WHEN THE LAYOUT ACTUALLY CHANGED, off the
    // same identity check. A sentence saying a section moved, printed over a
    // move that was a no-op, is the exact failure the receipt exists to
    // prevent — and it would be worse than saying nothing, because a person
    // would then stop checking.
    onReceipt(say);
  };

  return (
    <div class="rz__arr">
      <div class="rz__arr-head">
        <span class="rz__arr-eyebrow">Arrangement</span>
        {dirty ? (
          <div class="rz__arr-acts">
            <button type="button" class="rz__arr-save" disabled={saving} onClick={onSave}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" class="rz__arr-discard" disabled={saving} onClick={onDiscard}>
              Discard
            </button>
          </div>
        ) : null}
      </div>

      {/* ⚑ `printableSections` SAID OUT LOUD. The one thing a person managing
          a resume is actually managing is how much of it reaches the page,
          and until now that answer existed only as the sheet above — which
          you cannot count while you are turning rows off below it. */}
      <div class="rz__arr-prints">
        <span class="rz__arr-prints-k">On the sheet</span>
        <span class="rz__arr-prints-v">{printsLine(printable)}</span>
      </div>

      {/* ⚑ THIS ARRANGEMENT'S OWN FACE, and it is half of what a saved
          version is FOR: a theatre submission and a commercial submission are
          two different photographs. It lived behind "Name, headshot & retire"
          — three taps into a generic form, where a person who did not already
          know the feature existed would never find it. */}
      {looks.length > 0 ? (
        <div class="rz__arr-face">
          <div class="rz__arr-sec-head">
            <span class="rz__arr-sec-k">This one’s face</span>
          </div>
          <LookStrip
            tenantId={tenantId}
            looks={looks}
            chosenId={faceId}
            busyId={faceBusy}
            onChoose={onChooseFace}
          />
        </div>
      ) : null}

      {receipt ? (
        <div class="rz__receipt" role="status">
          {receipt}
        </div>
      ) : null}

      {error ? (
        <div class="rz__arr-error" role="alert">
          {error}
        </div>
      ) : null}

      {resolved.sections.map((section, i) => (
        <div class="rz__arr-sec" key={section.id}>
          <div class="rz__arr-sec-head">
            <span class="rz__arr-sec-k">{section.label}</span>
            {/* ⚑ THE SECTION'S ID, NEVER THE INDEX ON SCREEN. Resolution drops
                a section whose records have all gone, so this list and the
                stored one can differ by any number of entries — an index
                would move the wrong section, silently, on a resume. The
                DISABLED state may use the visible index, because it is only
                a hint: `apply` refuses a no-op either way. */}
            <button
              type="button"
              class="rz__arr-move"
              aria-label={`Move ${section.label} up`}
              disabled={i === 0}
              onClick={() =>
                apply(
                  moveSection(base, section.id, -1),
                  // ⚑ THE VISIBLE ORDER, not the stored one: resolution DROPS
                  // a section whose records have all gone, so the stored
                  // neighbour and the one a person can see are not always the
                  // same section — and the receipt has to name what they will
                  // see.
                  movedReceipt(visibleOrder, section.label, -1)
                )
              }
            >
              ↑
            </button>
            <button
              type="button"
              class="rz__arr-move"
              aria-label={`Move ${section.label} down`}
              disabled={i === resolved.sections.length - 1}
              onClick={() => apply(moveSection(base, section.id, 1), movedReceipt(visibleOrder, section.label, 1))}
            >
              ↓
            </button>
          </div>
          <ul class="rz__arr-rows">
            {section.rows.map((row) => (
              <li class={`rz__arr-row${row.excluded ? ' rz__arr-row--off' : ''}`} key={row.id}>
                <label class="rz__arr-check">
                  <input
                    type="checkbox"
                    checked={!row.excluded}
                    onChange={() =>
                      apply(
                        toggleRow(base, section.id, row.id),
                        toggledReceipt(row.lead, !row.excluded)
                      )
                    }
                  />
                  <span class="rz__arr-lead">{row.lead}</span>
                  <span class="rz__arr-mid">{row.middle}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {/* ⚑ THE TRAY — records this version has never been told about, because
          they did not exist when it was saved. THEY ARE NOT ON THE SHEET AND
          MUST NOT BE: a booking that lands on Tuesday silently inserting
          itself into the resume somebody sent on Monday is the failure this
          whole placement model exists to prevent. Here, where a person can
          see them and put them somewhere, is the honest place for them. */}
      {resolved.unplaced.length > 0 ? (
        <div class="rz__arr-tray">
          <div class="rz__arr-sec-head">
            <span class="rz__arr-sec-k">Not on this version</span>
          </div>
          <ul class="rz__arr-rows">
            {resolved.unplaced.map((entry) => (
              <li class="rz__arr-row" key={entry.row.id}>
                <span class="rz__arr-lead">{entry.row.lead}</span>
                <span class="rz__arr-mid">{entry.sectionLabel}</span>
                <button
                  type="button"
                  class="rz__arr-place"
                  onClick={() =>
                    apply(
                      placeRow(base, catalogue, entry.row.id),
                      placedReceipt(entry.row.lead, entry.sectionLabel)
                    )
                  }
                >
                  Add
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * THE NOTE A PERSON WROTE ABOUT AN ARRANGEMENT — "the one I send to
 * Soulpepper".
 *
 * ⚠ NEVER ON THE SHEET. The pack says so in as many words: a resume is the
 * one page that goes to a stranger, and this is the household talking to
 * itself. It sits above the document, outside `.print-resume`.
 *
 * Absent renders NOTHING — no label, no empty rule. A version nobody wrote a
 * note about is not a version with a missing note.
 */
function VersionNote({ version }: { version: RecordRow }) {
  const raw = version.fields.notes;
  const note = typeof raw === 'string' ? raw.trim() : '';
  if (note === '') return null;
  return <div class="rz__note">{note}</div>;
}

/**
 * W5 — THE THREE ADD DOORS. Not on the sheet: they sit under it, outside
 * `.print-resume`, so they are absent from the printed page for the same
 * reason the variant switcher is — everything that helped somebody get to
 * the document is scaffolding that must not appear on the thing they hand
 * over.
 *
 * ALWAYS ALL THREE, whether or not the block above has anything in it. A
 * door that appears only once the shelf is stocked is the shape of "you can
 * add a second one"; and the empty-resume branch renders this component too,
 * so a household on its first day still has somewhere to put a first class.
 *
 * NO CAPTION. Three buttons that say what they do need no sentence
 * explaining that a resume also holds training — a surface that has to teach
 * itself in prose has not taught itself.
 *
 * THE PERFORMER RIDES ALONG. Standing in one performer's resume, a new
 * record is created pre-linked to them through the pack's own relationship
 * key, which is the whole reason two children in one household do not print
 * one merged CV. Standing in the HOUSEHOLD there is no prefill and the form
 * asks — the honest answer, since nobody has said whose class it was.
 *
 * ⚑ W4 — A FOURTH DOOR, GATED ON THE PLAN READ, NOT ON A PAID TIER. A family
 * arriving with a career already on paper had no affordance that said so — a
 * resume assembled itself from bookings, or it did not exist, with nowhere
 * to hand over what the last agent already had. `requestDocumentCapture`
 * opens the SAME Knot sheet App.tsx already mounts, with the document kind
 * already decided — no second capture surface, and no callback threaded
 * through the shell. `resume.import_old` is Free-and-up (2026-09-24,
 * `defa9aed`), so `canImportOld` reads false only while the plan read is in
 * flight or has failed — the fallback line says so rather than naming a
 * tier nobody is actually missing.
 */
function FillDoors({ subjectId, canImportOld }: { subjectId: string | null; canImportOld: boolean }) {
  return (
    <div class="rz__fill">
      {RESUME_BLOCKS.map((block) => (
        <button
          type="button"
          class="rz__fill-b"
          key={block.key}
          onClick={() =>
            openRecordEditor({
              mode: 'create',
              typeKey: block.typeKey,
              // ⚑ KEYED BY THE PACK'S RELATIONSHIP KEY, WHICH RecordForm
              // reads by `schema.relationships[].key` — a wrong key here is
              // DISCARDED with no error and the form opens unlinked (the A4
              // session-prefill defect, packLinkKeys.test.ts). The keys come
              // from LINK, which that guard checks against the pack.
              ...(subjectId ? { prefillRels: { [block.subjectLink]: [subjectId] } } : {}),
            })
          }
        >
          {block.addLabel}
        </button>
      ))}
      {canImportOld ? (
        <button
          type="button"
          class="rz__fill-b"
          onClick={() => requestDocumentCapture('old_resume')}
        >
          Bring an old resume
        </button>
      ) : (
        <span class="rz__lapsed">
          An old resume comes back as credits — each one yours to approve — once your plan's confirmed.
        </span>
      )}
    </div>
  );
}
